/**
 * FAQ data access for the {@code FaqAccordion} blocks.
 *
 * <p>Single source of truth for how a block reaches the tenant's CMS
 * FAQ entries, so the shared block and the theme-scoped variants can't
 * drift:
 * <ul>
 *   <li>{@link fetchFaqPage} — one page of entries from the host's
 *       {@code POST /api/faq/search} BFF route (catalog-backed; carries
 *       the question + a short answer summary).</li>
 *   <li>{@link fetchFaqAnswer} — the full answer markup for one entry,
 *       from {@code GET /api/faq/answer/{slug}}. The catalog doesn't
 *       index bodies, so the full answer is fetched on expand.</li>
 * </ul>
 *
 * <p>Both fail soft: a non-OK response or a network error yields an
 * empty page / empty answer rather than throwing into a block render.
 */

/** One FAQ entry as the blocks consume it. */
export interface FaqEntry {
  id: string | number;
  /** Slug — the key {@link fetchFaqAnswer} loads the full answer by. */
  slug: string;
  question: string;
  /** Short answer summary from the list; may be markup-free. */
  answer: string;
  categoryId?: string | number | null;
  categoryName?: string | null;
}

export interface FaqPage {
  items: FaqEntry[];
  total: number;
}

/** A FAQ category for the sidebar filter — derived from the CMS, with a live count. */
export interface FaqCategory {
  id: string | number;
  label: string;
  /** Number of published FAQ entries in this category. */
  count: number;
}

export interface FaqPageQuery {
  /** Free-text search term. */
  term?: string;
  /** Narrow to a single category. */
  categoryId?: string | number | null;
  /**
   * Narrow to one course's FAQ. Absent means the PORTAL list.
   *
   * <p>The BFF turns this into the sub-type filter — the client never names
   * a sub-type, so it cannot pull course answers onto the public page.
   */
  courseId?: number | string | null;
  offset?: number;
  limit?: number;
}

const EMPTY_PAGE: FaqPage = { items: [], total: 0 };

function apiUrl(path: string): string {
  const base = (process.env['NEXT_PUBLIC_BASE_PATH'] ?? '').replace(/\/+$/, '');
  return `${base}${path}`;
}

/** Fetch one page of FAQ entries. Never throws — returns an empty page. */
export async function fetchFaqPage(query: FaqPageQuery = {}): Promise<FaqPage> {
  const body: Record<string, unknown> = {
    firstResult: Math.max(0, query.offset ?? 0),
    maxResults: Math.max(1, query.limit ?? 20),
  };
  if (query.term?.trim()) body['searchTerm'] = query.term.trim();
  if (query.categoryId != null) body['categoryIds'] = [query.categoryId];
  if (query.courseId != null) body['courseId'] = query.courseId;

  try {
    const res = await fetch(apiUrl('/api/faq/search'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    });
    if (!res.ok) return EMPTY_PAGE;
    const data = (await res.json()) as Partial<FaqPage>;
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total: typeof data.total === 'number' ? data.total : 0,
    };
  } catch {
    return EMPTY_PAGE;
  }
}

/**
 * Fetch the FAQ category list (id + label + count) for the sidebar
 * filter. Sourced from a facet over the FAQ catalog, so it always
 * mirrors the categories that actually have published content. Never
 * throws — returns an empty list (the block then hides the sidebar).
 */
export async function fetchFaqCategories(): Promise<FaqCategory[]> {
  try {
    const res = await fetch(apiUrl('/api/faq/categories'), { credentials: 'same-origin' });
    if (!res.ok) return [];
    const data = (await res.json()) as { categories?: FaqCategory[] };
    return Array.isArray(data.categories) ? data.categories : [];
  } catch {
    return [];
  }
}

/** Fetch the full answer markup for one entry. Never throws — returns ''. */
export async function fetchFaqAnswer(slug: string): Promise<string> {
  if (!slug) return '';
  try {
    const res = await fetch(apiUrl(`/api/faq/answer/${encodeURIComponent(slug)}`), {
      credentials: 'same-origin',
    });
    if (!res.ok) return '';
    const data = (await res.json()) as { answer?: unknown };
    return typeof data.answer === 'string' ? data.answer : '';
  } catch {
    return '';
  }
}

/** Upper bound on a course's FAQ list — see {@link fetchCourseFaqs}. */
const COURSE_FAQ_LIMIT = 50;

/**
 * One course's FAQ, resolved to FULL answers.
 *
 * <p>The catalog indexes title + excerpt but not bodies, so the list alone
 * would render a truncated answer. The portal /faq page hides that by
 * fetching the body on expand; the course-detail accordion renders its
 * answers inline, so they are resolved up front instead — bounded by
 * {@link COURSE_FAQ_LIMIT}, and falling back to the excerpt if a body fetch
 * fails so an entry never renders blank.
 *
 * <p>Never throws — an empty list simply hides the block.
 */
export async function fetchCourseFaqs(
  courseId: number | string,
): Promise<Array<{ question: string; answer: string }>> {
  if (courseId == null || courseId === '') return [];
  const page = await fetchFaqPage({ courseId, limit: COURSE_FAQ_LIMIT });
  if (page.items.length === 0) return [];
  return Promise.all(
    page.items.map(async (item) => ({
      question: item.question,
      answer: (await fetchFaqAnswer(item.slug)) || item.answer,
    })),
  );
}
